import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Button as MuiButton,
  Select,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Button from "../../../components/ui/Button";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import { tokens } from "../../../theme";
import {
  createSite,
  updateSite,
  SiteListApiItem,
} from "./services/siteListingConfig";

interface SiteFormData {
  name: string;
  url: string;
  category: string;
  status: "live" | "staging" | "";
  cover: File | null;
}

interface AddSiteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  site?: SiteListApiItem | null;
}

const CATEGORIES = [
  { label: "Livestreaming Company", value: "livestreaming_company" },
  { label: "Muzukuru", value: "muzukuru" },
  { label: "Chema", value: "chema" },
  { label: "Other", value: "other" },
];

const AddSiteDialog: React.FC<AddSiteDialogProps> = ({
  open,
  onClose,
  onSave,
  site,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEdit = Boolean(site);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);

  const [form, setForm] = useState<SiteFormData>({
    name: "",
    url: "",
    category: "",
    status: "",
    cover: null,
  });

  useEffect(() => {
    if (site) {
      setForm({
        name: site.name,
        url: site.domain,
        category: site.category,
        status: site.status as "live" | "staging",
        cover: null,
      });
      setCoverPreview(site.logo ?? null);
    } else {
      setForm({ name: "", url: "", category: "", status: "", cover: null });
      setCoverPreview(null);
    }
  }, [site, open]);

  const handleChange =
    (field: keyof SiteFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setForm((prev) => ({ ...prev, cover: file }));
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setCoverPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.url || !form.category || !form.status) {
      showErrorToast("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("domain", form.url);
      formData.append("category", form.category);
      formData.append("status", form.status);
      if (form.cover) formData.append("logo", form.cover);

      if (isEdit && site) {
        await updateSite(site.id, formData);
        showSuccessToast("Site updated successfully.");
      } else {
        await createSite(formData);
        showSuccessToast("Site added successfully.");
      }
      onSave();
    } catch (err: unknown) {
      const responseData = (
        err as { response?: { data?: Record<string, unknown> } }
      )?.response?.data;
      const errorMessage = responseData
        ? Object.values(responseData).flat().join(" ")
        : "Failed to save site. Please try again.";
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
      fontSize: 14,
      height: "46px",
      "& fieldset": { borderColor: "rgba(180, 195, 210, 0.6)" },
      "&:hover fieldset": { borderColor: colors.primary[400] },
      "&.Mui-focused fieldset": {
        borderColor: colors.primary[400],
        borderWidth: "1px",
      },
    },
    "& input": {
      py: 0,
      px: 2.5,
      height: "100%",
      color: colors.primary[700],
      "&::placeholder": {
        color: "rgba(140, 160, 180, 0.7)",
        opacity: 1,
      },
    },
  };

  const selectSx = {
    borderRadius: "50px",
    height: "46px",
    fontSize: 14,
    color: colors.primary[700],
    "& .MuiSelect-select": {
      py: 0,
      px: 2.5,
      height: "100%",
      display: "flex",
      alignItems: "center",
    },
    "& fieldset": { borderColor: "rgba(180, 195, 210, 0.6)" },
    "&:hover fieldset": { borderColor: colors.primary[400] },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary[400],
      borderWidth: "1px",
    },
  };

  const labelSx = {
    fontSize: 13,
    fontWeight: 600,
    color: colors.primary[700],
    mb: 0.75,
    ml: 1,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "32px",
          p: 1,
          backgroundColor: colors.primary[900],
          boxShadow: "0 10px 40px rgba(0,0,0,0.04)",
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            fontSize="1.5rem"
            sx={{ color: colors.primary[700] }}
          >
            {isEdit ? "Edit site" : "Add site"}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: `1.5px solid ${colors.primary[300]}50`,
              borderRadius: "50%",
              width: 32,
              height: 32,
              color: colors.primary[700],
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box mb={2.5}>
          <Typography sx={labelSx}>Website Name</Typography>
          <TextField
            fullWidth
            placeholder="e.g stream fund"
            value={form.name}
            onChange={handleChange("name")}
            sx={inputSx}
          />
        </Box>

        <Box mb={2.5}>
          <Typography sx={labelSx}>Site URL</Typography>
          <TextField
            fullWidth
            placeholder="muzukuru/stream-fund"
            value={form.url}
            onChange={handleChange("url")}
            sx={inputSx}
          />
        </Box>

        <Box mb={2.5}>
          <Typography sx={labelSx}>Category</Typography>
          <FormControl fullWidth>
            <Select
              displayEmpty
              value={form.category}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, category: e.target.value }))
              }
              sx={selectSx}
              renderValue={(val) =>
                val ? (
                  <Typography fontSize={14} sx={{ color: colors.primary[700] }}>
                    {val as string}
                    {/* {CATEGORIES.find((c) => c.value === val)?.label ?? (val as string)} */}
                  </Typography>
                ) : (
                  <Typography
                    fontSize={14}
                    sx={{ color: "rgba(140, 160, 180, 0.7)" }}
                  >
                    select category
                  </Typography>
                )
              }
            >
              {CATEGORIES.map((cat) => (
                <MenuItem
                  key={cat.value}
                  value={cat.value}
                  sx={{ fontSize: 14 }}
                >
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box mb={3}>
          <Typography sx={labelSx}>Status</Typography>
          <FormControl fullWidth>
            <Select
              displayEmpty
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  status: e.target.value as "live" | "staging" | "",
                }))
              }
              sx={selectSx}
              renderValue={(val) =>
                val ? (
                  <Typography fontSize={14} sx={{ color: colors.primary[700] }}>
                    {val as string}
                  </Typography>
                ) : (
                  <Typography
                    fontSize={14}
                    sx={{ color: "rgba(140, 160, 180, 0.7)" }}
                  >
                    select status
                  </Typography>
                )
              }
            >
              <MenuItem value="live" sx={{ fontSize: 14 }}>
                Live
              </MenuItem>
              <MenuItem value="staging" sx={{ fontSize: 14 }}>
                Staging
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box mb={4}>
          <input
            type="file"
            ref={fileInputRef}
            accept=".svg,.png,.jpg,.jpeg"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <Box display="flex" alignItems="center" gap={1.5} ml={0.5}>
            <MuiButton
              variant="contained"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.85rem",
                px: 2,
                py: 1,
                boxShadow: "none",
                whiteSpace: "nowrap",
                backgroundColor: colors.primary[400],
                color: colors.primary[900],
                "&:hover": {
                  backgroundColor: colors.primary[500],
                  boxShadow: "none",
                },
              }}
            >
              Upload Cover
            </MuiButton>
            {coverPreview ? (
              <Box
                component="img"
                src={coverPreview}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "8px",
                  objectFit: "cover",
                  border: `1px solid ${colors.primary[300]}30`,
                }}
              />
            ) : (
              <Typography
                sx={{ color: "rgba(120, 135, 150, 0.8)", fontSize: "0.75rem" }}
              >
                SVG, PNG, JPG (MAX. 800x400px)
              </Typography>
            )}
          </Box>
        </Box>

        <Box display="flex" gap={1.5} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              flex: 1,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.95rem",
              py: 1.2,
              px: 4,
              backgroundColor: "rgba(180, 195, 215, 0.5)",
              color: colors.primary[700],
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(180, 195, 215, 0.7)",
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              flex: 1,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.95rem",
              py: 1.2,
              px: 4,
              boxShadow: "none",
              backgroundColor: colors.primary[400],
              color: colors.primary[900],
              "&:hover": {
                backgroundColor: colors.primary[500],
                boxShadow: "none",
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} sx={{ color: colors.primary[900] }} />
            ) : isEdit ? (
              "Save"
            ) : (
              "Add"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddSiteDialog;
